import { FC, useMemo } from 'react';

import {
    Box,
    Chip,
    GridColDef,
    IconButton,
    Icons,
    TSLLink,
    Typography,
} from '@thirdspacelearning/library';

import { ProgrammeRecommendation } from '../../types';

import { CMSDataGrid, useDataGridToolbar } from '@shared';
import { Country } from '@types';

const sx = {
    root: { display: 'flex', flexDirection: 'column', gap: 2, mt: 2 },
};

type Props = {
    recommendations: ProgrammeRecommendation[];
    country: Country;
    onEdit: (recommendation: ProgrammeRecommendation) => void;
    onDelete: (recommendation: ProgrammeRecommendation) => void;
};

const ProgrammeRecommendationsTable: FC<Props> = ({
    recommendations,
    country,
    onEdit,
    onDelete,
}) => {
    const { filterPanelEl, slots, slotProps } = useDataGridToolbar();

    const columns = useMemo(
        (): GridColDef<ProgrammeRecommendation>[] => [
            {
                field: 'id',
                type: 'string',
                headerName: 'ID',
                headerAlign: 'left',
                align: 'left',
                sortable: true,
                maxWidth: 70,
            },
            {
                field: 'working_level',
                type: 'string',
                headerName: 'Working level',
                sortable: true,
                minWidth: 160,
                flex: 1,
            },
            {
                field: 'year_id',
                type: 'number',
                headerName: country === Country.GB ? 'Year group' : 'Grade',
                renderCell: ({ row }) =>
                    row.year_id ? (
                        <Chip
                            label={
                                country === Country.GB
                                    ? `Year ${row.year_id}`
                                    : `Grade ${row.year_id}`
                            }
                            variant="outlined"
                            size="small"
                        />
                    ) : (
                        '-'
                    ),
                sortable: true,
                minWidth: 120,
                flex: 1,
            },
            {
                field: 'programme_title',
                type: 'string',
                headerName: 'Target programme',
                renderCell: ({ row }) => (
                    <TSLLink
                        to={`/programmes/${row.programme_id}`}
                        variant="body2"
                        data-testid="recommendation-programme-link"
                    >
                        {row.programme_title}
                    </TSLLink>
                ),
                sortable: true,
                minWidth: 250,
                flex: 2,
            },
            {
                field: 'actions',
                type: 'string',
                display: 'flex',
                headerName: '',
                renderCell: ({ row }) => (
                    <Box>
                        <IconButton
                            aria-label="edit"
                            size="small"
                            onClick={() => onEdit(row)}
                            data-testid={`edit-recommendation-${row.id}`}
                        >
                            <Icons.Edit color="primary" />
                        </IconButton>
                        <IconButton
                            aria-label="delete"
                            size="small"
                            onClick={() => onDelete(row)}
                            data-testid={`delete-recommendation-${row.id}`}
                        >
                            <Icons.Delete color="error" />
                        </IconButton>
                    </Box>
                ),
                sortable: false,
                width: 104,
            },
        ],
        [country, onEdit, onDelete],
    );

    return (
        <Box sx={sx.root}>
            {recommendations.length === 0 && (
                <Typography variant="body2" data-testid="no-recommendations">
                    There are no recommendations for this region yet.
                </Typography>
            )}
            <Box id="filter-panel" ref={filterPanelEl} />
            <CMSDataGrid
                columns={columns}
                rows={recommendations}
                slots={slots}
                slotProps={slotProps}
                showToolbar
                disableColumnMenu
                disableColumnSelector
                disableDensitySelector
                disableRowSelectionOnClick
                disableColumnResize
                hideFooter
            />
        </Box>
    );
};

export default ProgrammeRecommendationsTable;
